import { EMPTY_VIEW, type StreamView } from "../lib/stream/view-state.ts";

/**
 * The observable store for the live VIEW the stream client produces (design 12.1, 12.3).
 *
 * Three stores cooperate for the live stream:
 * - `live-stream.ts` holds the CONNECTION state (connecting / connected / error, transport, resume
 *   token) that the connection indicator reads. The client writes it as it connects and resumes.
 * - This store holds the VIEW the screens render: the events ring, metric series, sources and
 *   alerts, plus the pause-buffer facts (how many deltas are parked, whether the operator is paused)
 *   and the gap-disclosed flag ("a gap happened; this view may be incomplete until a fresh snapshot
 *   settles it"). The Events screen (11.12) and the data-quality strip read it.
 * - `pause-buffer.ts` is the pure buffer the client owns internally; this store only publishes its
 *   observable facts (count, overflow→gap) so React can render them.
 *
 * It is a plain external store (not React state) so the non-React stream client can write to it and
 * any number of components subscribe through `useSyncExternalStore`. `getSnapshot` returns a stable
 * reference between changes. There is one store per app, created at bootstrap and provided through
 * React context (see `stream-view.tsx`).
 */

export interface StreamViewSnapshot {
  /** The live view (events, metrics, sources, alerts). Starts empty before the first snapshot. */
  readonly view: StreamView;
  /** True while the operator has paused live-tail; deltas are buffered, not shown (FR-13 AC2). */
  readonly paused: boolean;
  /** How many delta frames are parked in the pause buffer (the "N buffered" count, FR-9.3). */
  readonly bufferedCount: number;
  /**
   * True once a gap was disclosed: the pause buffer overflowed, so the client discarded an
   * incomplete range and is recovering with a fresh snapshot. The Events screen and data-quality
   * strip surface this; it clears when the fresh snapshot has settled the view. (design 12.3:
   * "unverifiable is never green").
   */
  readonly gapDisclosed: boolean;
}

const INITIAL: StreamViewSnapshot = {
  view: EMPTY_VIEW,
  paused: false,
  bufferedCount: 0,
  gapDisclosed: false,
};

type Listener = () => void;

export interface StreamViewStore {
  getSnapshot(): StreamViewSnapshot;
  subscribe(listener: Listener): () => void;
  /** Replace the live view (the client passes the reducer's next view). */
  setView(view: StreamView): void;
  /** Publish whether the operator is paused. */
  setPaused(paused: boolean): void;
  /** Publish the current buffered-delta count. */
  setBufferedCount(count: number): void;
  /** Disclose that a gap occurred (buffer overflow) or clear it once a fresh snapshot settled it. */
  setGapDisclosed(disclosed: boolean): void;
}

export function createStreamViewStore(): StreamViewStore {
  let snapshot: StreamViewSnapshot = INITIAL;
  const listeners = new Set<Listener>();

  const emit = (next: StreamViewSnapshot): void => {
    // Skip a notification when nothing actually changed, so a no-op frame (a deduped redelivery
    // returns the same view reference) does not re-render every subscriber.
    if (
      next.view === snapshot.view &&
      next.paused === snapshot.paused &&
      next.bufferedCount === snapshot.bufferedCount &&
      next.gapDisclosed === snapshot.gapDisclosed
    ) {
      return;
    }
    snapshot = next;
    for (const listener of listeners) listener();
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setView: (view) => emit({ ...snapshot, view }),
    setPaused: (paused) => emit({ ...snapshot, paused }),
    setBufferedCount: (bufferedCount) => emit({ ...snapshot, bufferedCount }),
    setGapDisclosed: (gapDisclosed) => emit({ ...snapshot, gapDisclosed }),
  };
}
